import {
  PlotAxisConfig,
  ViewportSyncMode,
} from "@/shared/state/visualization/schema/v1";
import { type MessagePayload, WorkerManager } from "@/shared/webworker";

import {
  PanPayload,
  PlotCommand,
  PlotEvent,
  SOURCE,
  ZoomPayload,
  isInitializedEvent,
  isLoadingStateChangeEvent,
  isRenderedEvent,
  type PlotCommandPayloadMap,
  type PlotEventPayloadMap,
  type PlotInit,
  type SetStatePayload,
} from "./messaging";
import { Axis } from "./messaging/commands";
import type { HoverDatum, Style } from "./PlotRenderer";
import { Viewport } from "./Viewport";

export type OnInitializedCallback = () => void;
export type OnErrorCallback = (
  error: PlotEventPayloadMap[PlotEvent.Error],
) => void;
export type OnLoadingCallback = (
  arg0: PlotEventPayloadMap[PlotEvent.LoadingStateChange],
) => void;
export type OnRenderCallback = (
  arg0: PlotEventPayloadMap[PlotEvent.Rendered],
) => void;

const noop = () => {};

/**
 * Coordinates the rendering of a plot from the UI thread.
 * Instantiates its counterpart in a web worker, forwards commands to it, and listens for events emitted by it.
 */
export class PlotManager {
  #abortController: AbortController;
  #onErrorCallback: OnErrorCallback = noop;
  #onInitialized: OnInitializedCallback = noop;
  #onLoading: OnLoadingCallback = noop;
  #onRender: OnRenderCallback = noop;
  #workerManager: WorkerManager;

  constructor(params: PlotInit) {
    this.#abortController = new AbortController();
    const worker = new Worker(
      new URL("./PlotManager.worker", import.meta.url),
      {
        type: "module",
        name: `PlotManager_${crypto.randomUUID()}`,
      },
    );
    this.#workerManager = new WorkerManager({
      worker,
      onError: this.#onError,
      onMessage: this.#onMessage,
      signal: this.#abortController.signal,
    });

    this.#send(PlotCommand.Init, params, {
      signal: this.#abortController.signal,
      transferables: [params.canvas],
    });
  }

  public getDataUnderCursor({
    x,
    y,
    signal,
  }: {
    x: number;
    y: number;
    signal: AbortSignal;
  }): Promise<HoverDatum[]> {
    return this.#workerManager.sendCommandAwaitResponse({
      type: PlotCommand.GetDataUnderCursor,
      data: { x, y },
      signal,
      source: SOURCE,
    });
  }

  public setEventListener<K extends keyof PlotEventPayloadMap>(
    type: K,
    listener: (arg0: PlotEventPayloadMap[K]) => void,
  ): void {
    switch (type) {
      case PlotEvent.Initialized:
        this.#onInitialized = listener as OnInitializedCallback;
        break;
      case PlotEvent.LoadingStateChange:
        this.#onLoading = listener as OnLoadingCallback;
        break;
      case PlotEvent.Rendered:
        this.#onRender = listener as OnRenderCallback;
        break;
      case PlotEvent.Error:
        this.#onErrorCallback = listener as OnErrorCallback;
        break;
      default:
        throw new Error(`Unknown event type: ${type}`);
    }
  }

  public dispose() {
    // Remove event listeners registered on worker
    this.#abortController.abort();

    // Allow worker to shut itself down gracefully
    this.#send(PlotCommand.Dispose, undefined);

    this.#workerManager.dispose();
  }

  public pan(payload: PanPayload): void {
    this.#send(PlotCommand.Pan, payload);
  }

  public applyViewport(viewport: Viewport, syncMode: ViewportSyncMode): void {
    this.#send(PlotCommand.ApplyViewport, {
      params: viewport.getParams(),
      syncMode,
    });
  }

  public resetView(): void {
    this.#send(PlotCommand.ResetView, undefined);
  }

  public resize({ width, height }: { width: number; height: number }): void {
    this.#send(PlotCommand.Resize, { width, height });
  }

  public setState(
    payload: SetStatePayload,
    { signal }: { signal: AbortSignal },
  ): void {
    this.#send(PlotCommand.SetState, payload, { signal });
  }

  public setStyle(style: Style): void {
    this.#send(PlotCommand.SetStyle, style);
  }

  public updateAxisConfig(
    axis: Axis,
    config: PlotAxisConfig,
    { overrideChartBounds }: { overrideChartBounds?: boolean } = {},
  ): void {
    const toSend: PlotAxisConfig = { label: config.label };
    if (config.bounds?.setBy !== "chart" || overrideChartBounds) {
      // Don't circular-update the bounds of any config.
      toSend.bounds = config.bounds;
    }
    this.#send(PlotCommand.UpdateAxisConfig, { axis, config: toSend });
  }

  public zoom(payload: ZoomPayload): void {
    this.#send(PlotCommand.Zoom, payload);
  }

  /**
   * Post a command to the worker and return without waiting for a response.
   * {@link PlotCommandPayloadMap} pairs each command with the payload type
   * the worker-side handler reads, so passing the wrong shape fails to
   * compile here instead of reaching that handler.
   */
  #send<C extends PlotCommand>(
    type: C,
    payload: PlotCommandPayloadMap[C],
    {
      signal,
      transferables,
    }: { signal?: AbortSignal; transferables?: Transferable[] } = {},
  ): void {
    this.#workerManager.sendCommand({
      data: payload,
      signal,
      source: SOURCE,
      transferables,
      type,
    });
  }

  #onMessage = (event: MessageEvent<MessagePayload>) => {
    if (isInitializedEvent(event)) {
      this.#onInitialized();
      return;
    }

    if (isLoadingStateChangeEvent(event)) {
      this.#onLoading(event.data.data);
      return;
    }

    if (isRenderedEvent(event)) {
      this.#onRender(event.data.data);
      return;
    }
  };

  #onError = (error: Error) => {
    this.#onErrorCallback(error);
  };
}
